import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAppSelector } from './store/hooks';
import { selectSignup } from './store/signupSlice';

// Layout
import MarketingLayout from './components/layout/MarketingLayout';
import OnboardingLayout from './components/layout/OnboardingLayout';

// Public Pages
import HomePage from './pages/HomePage';
import FeaturesPage from './pages/FeaturesPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import RequestDemoPage from './pages/RequestDemoPage';

// Legal Pages
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import CompliancePage from './pages/legal/CompliancePage';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';

// Onboarding Pages
import OnboardingPage from './pages/onboarding/OnboardingPage';
import PaymentPage from './pages/onboarding/PaymentPage';
import AwaitingVerificationPage from './pages/onboarding/AwaitingVerificationPage';
import WelcomePage from './pages/onboarding/WelcomePage';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, accessToken } = useAppSelector(selectSignup);

  if (!isAuthenticated && !accessToken) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Redirect if authenticated
const PublicOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, accessToken, status, emailVerified } = useAppSelector(selectSignup);

  if (isAuthenticated || accessToken) {
    // Redirect based on status
    if (!emailVerified) {
      return <Navigate to="/verify-email" replace />;
    }
    if (status === 'onboarding') {
      return <Navigate to="/onboarding" replace />;
    }
    if (status === 'pending_payment') {
      return <Navigate to="/onboarding/payment" replace />;
    }
    if (status === 'pending_approval') {
      return <Navigate to="/onboarding/pending" replace />;
    }
    if (status === 'approved' || status === 'active') {
      return <Navigate to="/welcome" replace />;
    }
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <Routes>
      {/* Public Marketing Pages */}
      <Route element={<MarketingLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/request-demo" element={<RequestDemoPage />} />
        {/* Pre-launch: pricing/trial flow hidden — everything funnels to the live demo. */}
        <Route path="/pricing" element={<Navigate to="/request-demo" replace />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        {/* Legal */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/compliance" element={<CompliancePage />} />
      </Route>

      {/* Auth Pages */}
      {/* Pre-launch: public signup hidden — funnel to the live demo. */}
      <Route path="/signup" element={<Navigate to="/request-demo" replace />} />
      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <MarketingLayout>
              <LoginPage />
            </MarketingLayout>
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/verify-email"
        element={
          <ProtectedRoute>
            <MarketingLayout>
              <VerifyEmailPage />
            </MarketingLayout>
          </ProtectedRoute>
        }
      />

      {/* Onboarding Pages */}
      <Route element={<OnboardingLayout />}>
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/payment"
          element={
            <ProtectedRoute>
              <PaymentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/pending"
          element={
            <ProtectedRoute>
              <AwaitingVerificationPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Welcome Page */}
      <Route
        path="/welcome"
        element={
          <ProtectedRoute>
            <WelcomePage />
          </ProtectedRoute>
        }
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
